import { z } from 'zod';

/**
 * Vague 7 (ajout 9) : **mode démonstration**, pour les rendez-vous commerciaux. Deux entrées :
 *
 * - **9a, démonstration générique publique** : bouton « Voir la démo » de la page de connexion
 *   (`DEMO_ENABLED`, `public.platform.demoEnabled`), sur un garage générique (`DEMO_GARAGE_SLUG`, nom fictif,
 *   mode de marque Discrète), réinitialisé chaque nuit. `demo.enter` : choix du rôle, sans compte.
 * - **9b, liens de démonstration brandés** créés depuis la console : un garage de démonstration
 *   (`garages.kind = 'demo'`, jamais compté comme un client, sans facturation) aux couleurs du prospect,
 *   rempli par le jeu de démonstration, réinitialisé chaque nuit **ou conservé** (`resetNightly = false`), et
 *   des **liens** partageables : jeton aléatoire **haché en base**, dans le **fragment** de l'adresse,
 *   expirant (14 jours par défaut), révocable, ouverture limitée par IP, comptage simple des ouvertures
 *   (aucun suivi individuel). `demo.link.inspect` (page d'arrivée aux couleurs du garage, choix du rôle) et
 *   `demo.link.enter` (session de démonstration sans compte). `demo.switchRole` : changer de rôle.
 *   `pnpm platform:mark-demo --slug … --confirm` (staging seulement) passe un garage existant en démonstration.
 *
 * Garde-fous (cœur) : un lien ou une session de démonstration ne fonctionne **que** sur un garage
 * `kind = 'demo'` ; aucune sortie réelle depuis un garage de démonstration (outbox marquée `demo_garage`,
 * aucun e-mail, push ni SMS) ; ni invitation, ni mot de passe, ni marque, ni console, ni accès support, ni
 * changement de garage pour une session de démonstration (403 `demo_forbidden`) ; écritures permises dans le
 * bac à sable, plafonnées par session (429 `demo_quota_reached`). Garde-fou S37 : aucune démonstration avec
 * `DEPLOY_ENV=production` (routes 404, console 409).
 *
 * Photos : **envoi réel permis**, plafonné (`photosPerSession`) et réduit (`photoMaxEdgePx`, originale non
 * gardée en pleine définition). Raison : le reportage photo est la fonction qui se montre en rendez-vous,
 * avec l'appareil photo du téléphone ; des photos fournies d'avance montreraient un faux geste. Le risque
 * (contenu déposé par un visiteur) est borné par la durée de session, le plafond, la limite par IP, la
 * réinitialisation de la nuit (garage générique) et la révocation des liens.
 */

/** `customer` : garage client (facturable, compté) ; `demo` : garage de démonstration. */
export const GarageKind = z.enum(['customer', 'demo']);
export type GarageKind = z.infer<typeof GarageKind>;

/** Rôles proposés en démonstration (pour montrer les droits). */
export const DEMO_ROLES = ['owner', 'frontdesk', 'mechanic'] as const;
export const DemoRole = z.enum(DEMO_ROLES);
export type DemoRole = z.infer<typeof DemoRole>;
export const DEMO_ROLE_LABELS: Record<DemoRole, string> = {
  owner: 'Propriétaire',
  frontdesk: 'Accueil',
  mechanic: 'Mécanicien',
};

/**
 * Spécialités d'un garage de démonstration brandé, pour adapter les données fictives (scénarios du jeu de
 * démonstration). Enregistrées dès la vague 7 ; l'adaptation des scénarios suit (`seed-demo-garage`).
 */
export const DemoSpecialty = z.enum(['general', 'prestige', 'sport', 'preparation', 'ppf', 'covering']);
export type DemoSpecialty = z.infer<typeof DemoSpecialty>;
export const DEMO_SPECIALTY_LABELS: Record<DemoSpecialty, string> = {
  general: 'Mécanique générale',
  prestige: 'Prestige',
  sport: 'Sport et collection',
  preparation: 'Préparation',
  ppf: 'Film de protection (PPF)',
  covering: 'Covering',
};

/**
 * 05/10/2026 : jeux de données fictives proposés par la console (« Remplir avec des données de test ») et
 * volumes. Toujours fictifs (adresses `@exemple.invalid`, plaques de séries non attribuées), jamais d'e-mail.
 */
export const DemoDataset = z.enum(['prestige', 'general']);
export type DemoDataset = z.infer<typeof DemoDataset>;
export const DEMO_DATASET_LABELS: Record<DemoDataset, string> = {
  prestige: 'Garage prestige / sport',
  general: 'Garage généraliste',
};
export const DemoVolume = z.enum(['light', 'standard', 'full']);
export type DemoVolume = z.infer<typeof DemoVolume>;
export const DEMO_VOLUME_LABELS: Record<DemoVolume, { label: string; description: string }> = {
  light: { label: 'Léger', description: '7 dossiers, 7 véhicules : un exemple par état.' },
  standard: {
    label: 'Standard',
    description: '14 dossiers sur un mois, 12 véhicules, tous les états de dossier et de travaux.',
  },
  full: {
    label: 'Complet',
    description: '23 dossiers, 16 véhicules, 13 clients : un mois complet et trois mois d’historique.',
  },
};

export const DemoEnterInput = z.strictObject({ role: DemoRole });

/** Jeton d'un lien de démonstration, lu dans le fragment de l'adresse et envoyé dans le corps (jamais dans l'URL). */
export const DemoLinkToken = z.string().min(20).max(200);
export const DemoLinkInspectInput = z.strictObject({ token: DemoLinkToken });
/** `role` absent : rôle par défaut du lien. */
export const DemoLinkEnterInput = z.strictObject({ token: DemoLinkToken, role: DemoRole.optional() });
export const DemoSwitchRoleInput = z.strictObject({ role: DemoRole });

/**
 * 02/10/2026 : image de marque propre à une session de démonstration (`demo.brandImage`), servie par l'API
 * au seul visiteur qui l'a envoyée (jamais une adresse publique).
 */
export const DemoBrandImageParams = z.strictObject({
  kind: z.enum(['logo', 'icon']),
  key: z.string().regex(/^[A-Za-z0-9_-]{20,64}$/),
});

/** Page d'arrivée d'un lien : marque du garage de démonstration et rôles proposés (aucune donnée de dossier). */
export const DemoLinkInspectOutput = z.object({
  garage: z.object({
    displayName: z.string(),
    primaryColor: z.string(),
    secondaryColor: z.string(),
    tertiaryColor: z.string().nullable(),
    brandMode: z.enum(['discreet', 'affirmed', 'immersive']),
    logoUrl: z.string().nullable(),
    iconUrl: z.string().nullable(),
  }),
  roles: z.array(DemoRole),
  defaultRole: DemoRole,
  expiresAt: z.iso.datetime({ offset: true }),
});
export type DemoLinkInspectOutput = z.infer<typeof DemoLinkInspectOutput>;

export const DEMO_LIMITS = {
  /** Durée d'une session de démonstration générique (minutes). */
  sessionMinutes: 120,
  /** Durée d'une session ouverte par un lien brandé (minutes) ; le lien se rouvre jusqu'à son échéance. */
  linkSessionMinutes: 480,
  /** Entrées en démonstration générique par IP et par heure. */
  enterPerIpPerHour: 20,
  /** Ouvertures (consultation ou entrée) d'un lien par IP et par minute. */
  linkOpenPerIpPerMinute: 20,
  /** Validité d'un lien : par défaut et au plus (jours). */
  linkDefaultDays: 14,
  linkMaxDays: 90,
  /** Dossiers créés par session (création directe et entrée d'un planifié). */
  interventionsPerSession: 10,
  /** Photos envoyées par session (reportage, photos de fiche). */
  photosPerSession: 30,
  /** Plus grand côté des photos gardées en démonstration (originale comprise), en pixels. */
  photoMaxEdgePx: 1600,
  /**
   * 02/10/2026 : apparence propre à la session (`garage.updateBranding` en démonstration). Logo et icône
   * réduits (plus grand côté, en pixels) et nombre d'envois (logo et icône confondus) par session.
   */
  brandImageMaxEdgePx: 256,
  brandImagesPerSession: 10,
} as const;

/** Bandeau de l'interface (consigne L5). */
export const DEMO_BANNER_TEXT = 'Démonstration · données fictives · réinitialisée chaque nuit';
/** Bandeau d'un garage de démonstration conservé (pas de réinitialisation). */
export const DEMO_BANNER_TEXT_KEPT = 'Démonstration · données fictives';
